import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Auth/Login';
import SignUp from './components/Auth/SignUp';
import ResetPassword from './components/Auth/ResetPassword';
import Dashboard from './components/Admin/Dashboard';
import Students from './components/Admin/Students';
import Parents from './components/Admin/Parents';
import Staff from './components/Admin/Staff';
import StaffHome from './components/Staff/StaffHome';
import AddStudent from './components/Staff/AddStudent';
import AddParent from './components/Staff/AddParent';
import EditStudent from './components/Staff/EditStudent';
import EditParent from './components/Staff/EditParent';
import Drafts from './components/Staff/Drafts';
import Sessions from './components/Admin/Sessions';
import DonorHome from './components/Donor/DonorHome';
import PaymentSettings from './components/Admin/PaymentSettings';
import StudentProgress from './components/Admin/StudentProgress';
import MonthlyReport from './components/Admin/MonthlyReport';
import Projects from './components/Admin/Projects';
import AllReports from './components/Admin/AllReports';
import AIInsights from './components/Admin/AIInsights';
import AIReportWriter from './components/Staff/AIReportWriter';
import AIAssistant from './components/Donor/AIAssistant';
import OnlinePayment from './components/Donor/OnlinePayment';
import ChessLobby from './components/Chess/ChessLobby';
import ChessRoom from './components/Chess/ChessRoom';
import ChessProfile from './components/Chess/ChessProfile';
import ChessReview from './components/Chess/ChessReview';
import ChessLearn from './components/Chess/ChessLearn';
import ChessPuzzle from './components/Chess/ChessPuzzle';
import TournamentLobby from './components/Chess/TournamentLobby';
import ChessAdmin from './components/Chess/ChessAdmin';
import TournamentDetail from './components/Chess/TournamentDetail';
import ChessGuide from './components/Chess/ChessGuide';
import ChessWatch from './components/Chess/ChessWatch';
import ChessHallOfFame from './components/Chess/ChessHallOfFame';
import ChessRanking from './components/Chess/ChessRanking';
import MaintenanceGate from './components/Shared/MaintenanceGate';
import MaintenanceSettings from './components/Admin/MaintenanceSettings';
import ReportTracking from './components/Admin/ReportTracking';
import ChessAccounts from './components/Admin/ChessAccounts';
import ChessStudents from './components/Admin/ChessStudents';
import Meetings from './components/Shared/Meetings';

// ── PROGRAMU (mpya) ────────────────────────────────────────────────────
import ProgramHub from './components/Programs/ProgramHub';
import ProgramDashboard from './components/Programs/ProgramDashboard';
import Members from './components/Programs/Members';
import MemberForm from './components/Programs/MemberForm';
import MemberDetail from './components/Programs/MemberDetail';
import BaselineForm from './components/Programs/BaselineForm';
import Clubs from './components/Programs/Clubs';
import WeeklyBoard from './components/Programs/WeeklyBoard';
import IndividualReport from './components/Programs/IndividualReport';
import SessionReport from './components/Programs/SessionReport';
import StaffWeekly from './components/Programs/StaffWeekly';
import ProgramAI from './components/Programs/ProgramAI';
import Announcements from './components/Programs/Announcements';
import CrossProgram from './components/Programs/CrossProgram';
import ProgramSettings from './components/Programs/ProgramSettings';
import DuplicateMembers from './components/Programs/DuplicateMembers';
import MyAccount from './components/MyAccount';
import OfficeChess from './components/OfficeChess';
import OfficeChessPrint from './components/OfficeChessPrint';
import { applyProgramOverrides } from './config/programs';
import { fetchProgramOverrides } from './utils/programData';

// ── IFR — Iyanna Foundation Rating (mpya) ──────────────────────────────
import RatingList from './components/Rating/RatingList';
import RatingSystem from './components/Rating/RatingSystem';
import PlayerRating from './components/Rating/PlayerRating';
import RatingAdmin from './components/Rating/RatingAdmin';
import LiveEvent from './components/Rating/LiveEvent';
import LiveEventPrint from './components/Rating/LiveEventPrint';
import RatingListPrint from './components/Rating/RatingListPrint';

function ProtectedRoute({ children, allowedRoles }) {
  const { currentUser, userRole } = useAuth();
  const location = useLocation();

  // Hajaingia kabisa → mpeleke kwenye LOGIN (si ukurasa wa mbele).
  // Tunahifadhi alipokuwa akienda ili arudishwe hapo baada ya kuingia.
  if (!currentUser) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Ameingia lakini nafasi yake bado haijulikani (mf. taarifa zake za
  // 'users' hazikusomeka). Tusimpeleke '/' — angebounce milele.
  if (!userRole) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (!allowedRoles.includes(userRole)) return <Navigate to="/" replace />;
  return children;
}

// ── Scroll to top on every route change ────────────────────────────────
// Without this, opening a new page (e.g. Edit Parent from a scrolled list)
// keeps the old scroll position, so the new page appears "cut off" and the
// top of the form is hidden above the screen.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Also reset the inner scrolling container used by dashboard layouts
    document.querySelectorAll('.main-content').forEach(el => { el.scrollTop = 0; });
  }, [pathname]);
  return null;
}

// ── Error Boundary — protects the whole app from white-screen crashes ──
// If any page throws an unexpected error, users see a friendly recovery
// screen instead of a blank page, and can navigate back safely.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('App crash caught by ErrorBoundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', padding: '24px',
          background: '#F6F4EE', fontFamily: "'Plus Jakarta Sans', sans-serif",
          textAlign: 'center',
        }}>
          <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: '24px', color: '#14452F', marginBottom: '10px' }}>
            Samahani, kuna hitilafu imetokea
          </div>
          <div style={{ fontSize: '13.5px', color: '#6B7280', maxWidth: '400px', lineHeight: 1.7, marginBottom: '22px' }}>
            Ukurasa huu umekumbwa na tatizo lisilotarajiwa. Bofya hapa chini kurudi mwanzo — hakuna data iliyopotea.
          </div>
          <button
            onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }}
            style={{
              background: 'linear-gradient(135deg, #14452F, #1F6B45)', color: '#fff',
              border: 'none', padding: '13px 26px', borderRadius: '11px',
              fontSize: '14px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Rudi Mwanzo
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppRoutes() {
  const { currentUser, userRole } = useAuth();

  return (
    <MaintenanceGate currentUser={currentUser} userRole={userRole}>
    <Routes>

      {/* PUBLIC ROUTES — DonorHome is now the public landing page, no login required */}
      <Route
        path="/"
        element={
          currentUser ? (
            userRole === 'admin' ? <Navigate to="/admin" /> :
            userRole === 'staff' ? <Navigate to="/staff" /> :
            userRole === 'donor' ? <Navigate to="/donor" /> :
            <DonorHome />
          ) : (
            <DonorHome />
          )
        }
      />
      {/* /login INAFUNGUKA DAIMA — haimrudishi mtu popote.
          Zamani ilikuwa inamrusha aliyekwisha kuingia kwenda kwenye
          ukurasa wake. Matokeo yake: mtu aliyeingia kama donor
          hakuweza KAMWE kufika hapa kubadili akaunti — alikuwa
          anarushwa /donor kila mara. Sasa Login yenyewe ndiyo
          inayomwonyesha hali yake na kumpa chaguo. */}
      <Route path="/login" element={<Login />} />
      <Route
        path="/signup"
        element={
          currentUser ? <Navigate to="/" /> : <SignUp />
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />

      {/* ADMIN ROUTES */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/students" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Students />
        </ProtectedRoute>
      } />
      <Route path="/admin/parents" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Parents />
        </ProtectedRoute>
      } />
      <Route path="/admin/staff" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Staff />
        </ProtectedRoute>
      } />
      <Route path="/admin/sessions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Sessions />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AllReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-insights" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AIInsights />
        </ProtectedRoute>
      } />
      <Route path="/admin/progress" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <StudentProgress />
        </ProtectedRoute>
      } />
      <Route path="/admin/monthly-reports" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <MonthlyReport />
        </ProtectedRoute>
      } />
      <Route path="/admin/projects" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Projects />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <PaymentSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/chess-students" element={
        <ProtectedRoute allowedRoles={['admin', 'staff']}>
          <ChessStudents />
        </ProtectedRoute>
      } />
      <Route path="/admin/maintenance" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <MaintenanceSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/report-tracking" element={
        <ProtectedRoute allowedRoles={['admin', 'staff']}>
          <ReportTracking />
        </ProtectedRoute>
      } />
      <Route path="/admin/chess-accounts" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ChessAccounts />
        </ProtectedRoute>
      } />

      {/* EDIT ROUTES - Both Admin and Staff */}
      <Route path="/edit-student/:id" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <EditStudent />
        </ProtectedRoute>
      } />
      <Route path="/edit-parent/:id" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <EditParent />
        </ProtectedRoute>
      } />

      {/* STAFF ROUTES */}
      <Route path="/staff" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <StaffHome />
        </ProtectedRoute>
      } />
      <Route path="/staff/students" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <Students />
        </ProtectedRoute>
      } />
      <Route path="/staff/parents" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <Parents />
        </ProtectedRoute>
      } />
      <Route path="/staff/add-student" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <AddStudent />
        </ProtectedRoute>
      } />
      <Route path="/staff/add-parent" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <AddParent />
        </ProtectedRoute>
      } />
      {/* Fomu za zamani zimeondolewa — sasa ripoti zinaandikwa kwa programu */}
      <Route path="/staff/write-report" element={<Navigate to="/programs" replace />} />
      <Route path="/staff/record-session" element={<Navigate to="/programs" replace />} />
      <Route path="/staff/drafts" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <Drafts />
        </ProtectedRoute>
      } />
      <Route path="/staff/ai-report" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <AIReportWriter />
        </ProtectedRoute>
      } />
      <Route path="/staff/meetings" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <Meetings />
        </ProtectedRoute>
      } />
      <Route path="/admin/meetings" element={
        <ProtectedRoute allowedRoles={['admin', 'staff']}>
          <Meetings />
        </ProtectedRoute>
      } />

      {/* ══ PROGRAMU — Chess, Msaada, Usa River YDC ══════════════════ */}
      <Route path="/programs" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <ProgramHub />
        </ProtectedRoute>
      } />
      <Route path="/programs/insights" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <CrossProgram />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <ProgramDashboard />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/members" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <Members />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/members/new" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <MemberForm />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/members/:mid" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <MemberDetail />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/members/:mid/edit" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <MemberForm />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/members/:mid/baseline" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <BaselineForm />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/clubs" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <Clubs />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/week" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <WeeklyBoard />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/report/:mid" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <IndividualReport />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/session" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <SessionReport />
        </ProtectedRoute>
      } />
      <Route path="/programs/:pid/ai" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <ProgramAI />
        </ProtectedRoute>
      } />
      <Route path="/staff/weekly" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <StaffWeekly />
        </ProtectedRoute>
      } />
      <Route path="/announcements" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <Announcements />
        </ProtectedRoute>
      } />
      <Route path="/admin/program-names" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ProgramSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/duplicate-members" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DuplicateMembers />
        </ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <MyAccount />
        </ProtectedRoute>
      } />
      <Route path="/office-chess" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <OfficeChess />
        </ProtectedRoute>
      } />
      <Route path="/office-chess/:id/print" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <OfficeChessPrint />
        </ProtectedRoute>
      } />
      {/* Kuhamisha data si hatua tena — App inafanya yenyewe */}
      <Route path="/admin/migration" element={<Navigate to="/programs/msaada/members" replace />} />

      {/* DONOR ROUTES — /donor is public, no login required */}
      <Route path="/donor" element={<DonorHome />} />
      <Route path="/donor/ai-assistant" element={<AIAssistant />} />
      <Route path="/donor/payment" element={
        <ProtectedRoute allowedRoles={['donor']}>
          <OnlinePayment />
        </ProtectedRoute>
      } />

      {/* CHESS — public, no login required */}
      <Route path="/chess" element={<ChessLobby />} />
      <Route path="/chess/guide" element={<ChessGuide />} />
      <Route path="/chess/watch" element={<ChessWatch />} />
      <Route path="/chess/more" element={<Navigate to="/chess/profile" replace />} />
      <Route path="/chess/hall-of-fame" element={<ChessHallOfFame />} />
      <Route path="/chess/ranking" element={<ChessRanking />} />
      <Route path="/chess/room/:id" element={<ChessRoom />} />
      <Route path="/chess/profile" element={<ChessProfile />} />
      <Route path="/chess/review/:id" element={<ChessReview />} />
      <Route path="/chess/learn" element={<ChessLearn />} />
      <Route path="/chess/puzzle" element={<ChessPuzzle />} />
      <Route path="/chess/admin" element={<ChessAdmin />} />
      {/* ══ IFR — rating rasmi. Orodha na kanuni ni za HADHARANI ══ */}
      <Route path="/rating" element={<RatingList />} />
      <Route path="/rating/system" element={<RatingSystem />} />
      <Route path="/rating/player/:id" element={<PlayerRating />} />
      <Route path="/rating/admin" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <RatingAdmin />
        </ProtectedRoute>
      } />
      <Route path="/rating/live" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <LiveEvent />
        </ProtectedRoute>
      } />
      <Route path="/rating/live/:id/print" element={
        <ProtectedRoute allowedRoles={['staff', 'admin']}>
          <LiveEventPrint />
        </ProtectedRoute>
      } />
      <Route path="/rating/print" element={<RatingListPrint />} />

      <Route path="/chess/tournaments" element={<TournamentLobby />} />
      <Route path="/chess/tournaments/:id" element={<TournamentDetail />} />

      {/* CATCH ALL */}
      <Route path="*" element={<Navigate to="/" />} />

    </Routes>
    </MaintenanceGate>
  );
}

// Inapakia majina ya programu yaliyowekwa na taasisi kabla kurasa
// hazijaonyeshwa. Yakikosekana (au mtumiaji hana ruhusa), tunatumia
// majina ya asili — App haisimami kamwe kwa sababu hii.
function ProgramNamesGate({ children }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchProgramOverrides()
      .then(list => {
        if (!alive || !list?.length) return;
        applyProgramOverrides(list);
        setTick(t => t + 1);
      })
      .catch(() => { /* majina ya asili yataendelea kutumika */ });
    return () => { alive = false; };
  }, []);

  return children;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <ProgramNamesGate>
            <AppRoutes />
          </ProgramNamesGate>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}